class Customizer {
  constructor() {
    this.selected = new Set();
    this.stepSelections = new Map(); // Track selections per step
    this.initialModalSelections = new Map(); // Track initial selections when modal opens
    this.initialSliderSelections = new Map(); // Store initial slider selections
    this.appliedSelections = new Map(); // Store applied selections per modal (for card display)
    this.variablePricing = document.querySelector('.variable-pricing') ? true : false;
    this.init();
  }

  init() {
    this.setupEventListeners();
    // Update initial price display
    this.updateAddToCartPrice();
  }

  // Add or remove a selection from a specific step
  updateStepSelection(stepId, variantId, isSelected, allowMultiple) {
    if (!allowMultiple && isSelected) {
      // Clear all other selections in this step first (but not the current one)
      const stepElement = document.querySelector(`.step[data-step-id="${stepId}"]`);
      if (stepElement) {
        stepElement.querySelectorAll('.customizer-option.selected, .customizer-product-option.selected').forEach(option => {
          const optionVariantId = option.dataset.variantid;
          if (optionVariantId !== variantId) {
            this.selected.delete(optionVariantId);
            option.classList.remove('selected');
          }
        });
      }
      
      // Clear the step tracking for this step
      this.clearStepSelections(stepId);
    }

    if (!this.stepSelections.has(stepId)) {
      this.stepSelections.set(stepId, new Set());
    }
    
    const stepSet = this.stepSelections.get(stepId);
    if (isSelected) {
      stepSet.add(variantId);
    } else {
      stepSet.delete(variantId);
    }
  }

  // Remove the no-product-selected class from all options
  removeNoProductSelectedClass(stepId) {
    const stepElement = document.querySelector(`.step[data-step-id="${stepId}"]`);
    stepElement.querySelectorAll('.customizer-option.no-product-selected').forEach(option => {
      option.classList.remove('no-product-selected');
    });
    stepElement.querySelector('.no-product-button').classList.remove('selected');
  }

  // Check if step has at least one selection
  isStepValid(stepId) {
    const stepSet = this.stepSelections.get(stepId);
    return stepSet && stepSet.size > 0;
  }

  // Clear all selections for a specific step (for "No [product]" options)
  clearStepSelections(stepId) {
    const stepSet = this.stepSelections.get(stepId);
    if (stepSet) {
      // Remove all step selections from global selected set
      stepSet.forEach(variantId => {
        this.selected.delete(variantId);
      });
      stepSet.clear();
    }
  }

  // Validate all steps have selections
  validateAllSteps() {
    const steps = document.querySelectorAll('.step[data-step-id]');
    const errors = [];
    
    steps.forEach(step => {
      const stepId = step.dataset.stepId;
      if (!this.isStepValid(stepId)) {
        errors.push(stepId);
        this.showStepError(stepId, 'Please select at least one option from this step.');
      } else {
        this.hideStepError(stepId);
      }
    });
    
    // If there are errors, scroll to the first one
    if (errors.length > 0) {
      this.scrollToFirstError(errors[0]);
    }
    
    return errors.length === 0;
  }

  // Show error message for a step
  showStepError(stepId, message) {
    const step = document.querySelector(`.step[data-step-id="${stepId}"]`);
    if (step) {
      const errorElement = step.querySelector('.step-error-message');
      if (errorElement) {
        errorElement.textContent = message;
        errorElement.style.display = 'block';
      }
    }
  }

  // Hide error message for a step
  hideStepError(stepId) {
    const step = document.querySelector(`.step[data-step-id="${stepId}"]`);
    if (step) {
      const errorElement = step.querySelector('.step-error-message');
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }
  }

  // Scroll to the first error step
  scrollToFirstError(stepId) {
    const step = document.querySelector(`.step[data-step-id="${stepId}"]`);
    if (step) {
      // Add a small delay to ensure the error message is displayed
      setTimeout(() => {
        step.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
    }
  }

  // Get sections to render (same as ProductForm)
  getSectionsToRender() {
    return [{
      id: 'Cart',
      section: 'main-cart',
      selector: '.thb-cart-form'
    },
    {
      id: 'Cart-Drawer',
      section: 'cart-drawer',
      selector: '.cart-drawer'
    },
    {
      id: 'cart-drawer-toggle',
      section: 'cart-bubble',
      selector: '.thb-item-count'
    }];
  }

  // Get section inner HTML (same as ProductForm)
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  // Render contents (same as ProductForm)
  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section => {
      if (!document.getElementById(section.id)) {
        return;
      }
      const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

      if(elementToReplace.classList.contains('thb-item-count')) {
         const q = parseInt(elementToReplace.innerText, 10);
         if (!isNaN(q) && q > 0) {
           elementToReplace.classList.add('red-bubble');
         } else {
           elementToReplace.classList.remove('red-bubble');
         }
      }

      if (typeof CartDrawer !== 'undefined') {
        new CartDrawer();
      }
      if (typeof Cart !== 'undefined') {
        new Cart().renderContents(parsedState);
      }
    }));
  }

  updateApplyButton(id) {
    const modal = document.getElementById(id);
    if (modal) {
      const applyBtn = modal.querySelector('.modal-apply-btn');
      if (applyBtn) {
        const selectedOptionsInModal = modal.querySelectorAll('.customizer-option.selected, .customizer-product-option.selected')?.length || 0;
        const hadInitialSelections = this.initialModalSelections.get(id) || 0;
        
        // Check if this is a slider modal with selected products
        let selectedProductsInSlider = 0;
        if (window.customizerCollectionModalSlider && window.customizerCollectionModalSlider.selectedProducts) {
          selectedProductsInSlider = window.customizerCollectionModalSlider.selectedProducts.size;
        }

        const hasAnySelections = selectedOptionsInModal > 0 || selectedProductsInSlider > 0 || hadInitialSelections > 0;
        applyBtn.disabled = !hasAnySelections;
      }
    }
  }

  updateSelectedPrice(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
      const selectedOptions = modal.querySelectorAll('.customizer-option.selected, .customizer-product-option.selected');
      let total = 0;
      
      selectedOptions.forEach(option => {
        const price = parseFloat(option.dataset.price) || 0;
        total += price;
      });

      const optionId = modalId.replace('collection-modal-', '');
      const collectionCard = document.querySelector(`[data-optionID="${optionId}"]`);
      if (collectionCard) {
        const priceElement = collectionCard.querySelector('.selected-price-text');
        if (priceElement) {
          if (total > 0) {
            priceElement.textContent = `+${total.toFixed(2)} USD`;
          } else {
            priceElement.textContent = '+0.00 USD';
          }
        }
      }
    }
  }

  saveAppliedSelections(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
      // Save grid selections
      const selectedOptions = modal.querySelectorAll('.customizer-option.selected, .customizer-product-option.selected');
      const gridSelections = Array.from(selectedOptions).map(option => ({
        variantId: option.dataset.variantid,
        price: option.dataset.price
      }));
      
      // Save slider selections (copy current state)
      const sliderSelections = new Map();
      if (window.customizerCollectionModalSlider?.selectedProducts) {
        window.customizerCollectionModalSlider.selectedProducts.forEach((variant, variantId) => {
          const cardInModal = variant.modalId === modalId;
          
          if (cardInModal) {
            sliderSelections.set(variantId, { ...variant });
          }
        });
      }
      
      // Store applied selections for this modal
      this.appliedSelections.set(modalId, {
        grid: gridSelections,
        slider: sliderSelections
      });
      
      // Update the add to cart price after applying selections
      this.updateAddToCartPrice(true);
    }
  }

  updateCardTitle(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
      // Use applied selections instead of current selections
      const appliedSelections = this.appliedSelections.get(modalId);
      
      if (appliedSelections) {
        // Count grid selections
        const gridCount = appliedSelections.grid.length;
        
        // Count slider selections
        let totalSliderQuantity = 0;
        appliedSelections.slider.forEach((variant) => {
          totalSliderQuantity += variant.quantity;
        });
        
        const totalSelectedCount = gridCount + totalSliderQuantity;
        
        const optionId = modalId.replace('collection-modal-', '');
        const collectionCard = document.querySelector(`[data-optionID="${optionId}"]`);
        
        if (collectionCard) {
          const originalName = collectionCard.dataset.originalName;
          const title = collectionCard.querySelector('.customizer-option-title');
          
          const displayText = totalSelectedCount > 0 ? `${totalSelectedCount} Item${totalSelectedCount > 1 ? 's' : ''} Selected` : originalName;
          
          if (title) {
            title.textContent = displayText;
          }
        }
      } else {
        // No applied selections, show original name
        const optionId = modalId.replace('collection-modal-', '');
        const collectionCard = document.querySelector(`[data-optionID="${optionId}"]`);
        
        if (collectionCard) {
          const originalName = collectionCard.dataset.originalName;
          const title = collectionCard.querySelector('.customizer-option-title');
          
          if (title) {
            title.textContent = originalName;
          }
        }
      }
    }
  }

  updateStepSummary(modalId) {
    const appliedSelections = this.appliedSelections.get(modalId).slider;
    const wrapper = document.querySelector('.step-modal-selections');
    const selectedProducts = wrapper.querySelector('.selected-products');
    const selectedProductsCards = wrapper.querySelector('.selected-products-cards');
    const selectedProductsSummary = wrapper.querySelector('.selected-products-summary');

    if (appliedSelections.size === 0) {
      selectedProducts.classList.remove('selected-products--visible');
    } else {
      selectedProducts.classList.add('selected-products--visible');

      // Update count and total
      let totalCount = 0;
      let totalPrice = 0;
      
      appliedSelections.forEach((variant) => {
        totalCount += variant.quantity;

        const priceValue = parseFloat(variant.price.replace(/[^0-9.]/g, '')) || 0;
        totalPrice += priceValue * variant.quantity;
      });

      // Update summary
      selectedProductsSummary.innerHTML = `${totalCount} Ring${ totalCount > 1 ? 's' : '' } Selected`;

      // Remove existing product cards (keep summary and structure)
      const existingCards = selectedProductsCards.querySelectorAll('.selected-product-card');
      existingCards.forEach(card => card.remove());
      
      // Add new product cards
      appliedSelections.forEach((item) => {
        const productCard = document.createElement('div');
        productCard.className = 'selected-product-card';
        productCard.dataset.variantId = item.variantId;
        
        productCard.innerHTML = `
          <div class="selected-product-card__content">
            <img class="selected-product-card__image" src="${item.image}" alt="${item.title}">
            <p class="selected-product-card__title">${item.title} (${item.quantity})</p>
          </div>
          <button type="button" class="button grey selected-product-card__remove"><span>&times;</span></button>
        `;
        
        selectedProductsCards.appendChild(productCard);
      });
    }
  }

  updateCollectionCardButton(collectionCard, newText) {
    // The CSS shows/hides the appropriate spans based on the .selected class
    if (newText) {
      // When there are selections, add the selected class to show "View/Modify" text
      collectionCard.classList.add("selected");
    } else {
      // When no selections, remove the selected class to show original text
      collectionCard.classList.remove("selected");
    }
  }

  updateCardPrice(modalId) {
    const modal = document.getElementById(modalId);
    if (modal) {
      const optionId = modalId.replace('collection-modal-', '');
      const collectionCard = document.querySelector(`[data-optionID="${optionId}"]`);
      
      if (collectionCard) {
        const priceElement = collectionCard.querySelector('.selected-price-text');
        if (priceElement) {
          let totalPrice = 0;
          
          // Use applied selections instead of current selections
          const appliedSelections = this.appliedSelections.get(modalId);
          
          if (appliedSelections) {
            // Calculate price from applied grid selections
            appliedSelections.grid.forEach(option => {
              const price = parseFloat(option.price) || 0;
              totalPrice += price;
            });
            
            // Calculate price from applied slider selections
            appliedSelections.slider.forEach((variant) => {
              const priceValue = parseFloat(variant.price.replace(/[^0-9.]/g, '')) || 0;
              totalPrice += priceValue * variant.quantity;
            });
          }
          
          if (totalPrice > 0) {
            priceElement.textContent = `+${totalPrice.toFixed(2)} USD`;
          } else {
            priceElement.textContent = '+0.00 USD';
          }
        }
      }
    }
  }

  handleModalClose(id) {
    if (id) {
      const modal = document.getElementById(id);
      if (modal) {
        modal.style.display = 'none';
        const optionsWrapper = modal.parentElement;
        optionsWrapper.querySelectorAll('.customizer-option').forEach(card => {
          card.style.display = 'flex';
        })

        this.initialModalSelections.delete(id);
      }
    }
  }

  setupEventListeners() {
    // Single delegated click listener for all customizer interactions
    document.addEventListener("click", (e) => {
      const target = e.target;

      // Handle remove button clicks within the step modal selections summary
      if (target.closest(".step-modal-selections .selected-product-card__remove")) {
        e.preventDefault();
        e.stopPropagation();
        const card = target.closest(".selected-product-card");
        const variantId = card?.dataset.variantId;
        if (variantId && window.customizerCollectionModalSlider) {
          window.customizerCollectionModalSlider.removeProductFromSelected(variantId);
        }
        const optionID = target.closest(".step-modal-selections").dataset.optionid;
        const modal = document.getElementById(`collection-modal-${optionID}`);
        if (modal) {
          const applyBtn = modal.querySelector(".modal-apply-btn");
          if (applyBtn) applyBtn.click();
        }
      }

      // Handle customizer collection card click (modal opening)
      else if (target.closest(".customizer-collection-option")) {
        e.preventDefault();
        const item = target.closest(".customizer-collection-option");
        const optionId = item.dataset.optionid;
        const modalId = `collection-modal-${optionId}`;
        const modal = document.getElementById(modalId);
        const optionCard = document.querySelector(`.customizer-collection-option[data-optionID="${optionId}"]`);
        
        if (optionCard) {
          const optionsWrapper = optionCard.parentElement;
          optionsWrapper.querySelectorAll('.customizer-option').forEach(card => {
            card.style.display = 'none';
          });
        }

        if (modal) {
          const initialSelectedCount = modal.querySelectorAll('.customizer-option.selected, .customizer-product-option.selected')?.length || 0;
          this.initialModalSelections.set(modalId, initialSelectedCount);
          
          // Store initial slider selections
          if (window.customizerCollectionModalSlider) {
            const initialSliderSelections = new Map(window.customizerCollectionModalSlider.selectedProducts);
            this.initialSliderSelections.set(modalId, initialSliderSelections);
          }
          
          modal.style.display = 'block';
          this.updateApplyButton(modalId);
        }
      }

      // Handle customizer option clicks in modal (direct options, not collection options)
      else if (target.closest(".customizer-option, .customizer-product-option")) {
        const item = target.closest(".customizer-option, .customizer-product-option");
        const variantId = item.dataset.variantid;
        const stepId = item.dataset.stepId;
        const allowMultiple = item.closest(".customizer-step").dataset.allowMultiple === 'true';
        
        if (this.selected.has(variantId)) {
          this.selected.delete(variantId);
          item.classList.remove("selected");
          this.updateStepSelection(stepId, variantId, false, allowMultiple);
        } else {
          this.selected.add(variantId);
          item.classList.add("selected");
          this.updateStepSelection(stepId, variantId, true, allowMultiple);
          this.removeNoProductSelectedClass(stepId);
        }

        // Hide step error when selection is made
        if (stepId) {
          this.hideStepError(stepId);
        }

        const modal = e.target.closest('[id^="collection-modal-"]');
        if (modal) {
          const modalId = modal.getAttribute('id');
          this.updateApplyButton(modalId);
          this.updateSelectedPrice(modalId);
          this.updateCardTitle(modalId);
          this.updateStepSummary(modalId);
          // Update add to cart price in real-time with a small delay to ensure DOM is updated
          setTimeout(() => {
            this.updateAddToCartPrice(true);
          }, 10);
        } else {
          let parent = e.target.closest('.customizer-option').parentElement;
          let level = 0;
          while (parent && parent !== document.body && level < 10) {
            parent = parent.parentElement;
            level++;
          }
          // Update add to cart price even if no modal found (for direct customizer options)
          setTimeout(() => {
            this.updateAddToCartPrice(true);
          }, 10);
        }
      }

      // Handle modal cancel button clicks
      else if (target.closest(".modal-cancel-btn, .modal-close-btn")) {
        const modal = e.target.closest('[id^="collection-modal-"]');
        const modalId = modal.getAttribute('id');
        const optionId = modalId.replace('collection-modal-', '');

        if (modal) {
          // Clear grid selections
          modal.querySelectorAll(".customizer-option.selected, .customizer-product-option.selected").forEach(item => {
            const variantId = item.dataset.variantid;
            this.selected.delete(variantId);
            item.classList.remove("selected");
          });
          
          // Restore initial slider selections
          if (window.customizerCollectionModalSlider) {
            const initialSliderSelections = this.initialSliderSelections.get(modalId);
            if (initialSliderSelections) {
              // Restore the initial state
              window.customizerCollectionModalSlider.selectedProducts.clear();
              initialSliderSelections.forEach((value, key) => {
                window.customizerCollectionModalSlider.selectedProducts.set(key, value);
              });
            } else {
              // If no initial state, clear everything
              window.customizerCollectionModalSlider.selectedProducts.clear();
            }
            
            window.customizerCollectionModalSlider.updateSelectedProductsDisplay();
            
            // Update all slider card states based on restored selections
            const sliderCards = modal.querySelectorAll('.slider-card');
            sliderCards.forEach(card => {
              const variantId = card.dataset.variantId;
              const isSelected = window.customizerCollectionModalSlider.selectedProducts.has(variantId);
              window.customizerCollectionModalSlider.updateAddButtonState(card, isSelected);
            });
          }
          
          // Don't reset collection card state - preserve previously applied selections
          // Only close the modal without updating the card
          this.handleModalClose(modalId);
          
          // Clean up initial selections
          this.initialModalSelections.delete(modalId);
          this.initialSliderSelections.delete(modalId);
        }
      }

      // Handle modal apply button clicks
      else if (target.closest(".modal-apply-btn")) {
        const button = target.closest(".modal-apply-btn");
        if (!button.disabled) {
          const modal = e.target.closest('[id^="collection-modal-"]');
          if (modal) {
            const modalId = modal.getAttribute('id');
            const optionId = modalId.replace('collection-modal-', '');
            const stepId = target.closest(".customizer-step").dataset.stepId;

            const collectionCard = document.querySelector(`[data-optionID="${optionId}"]`);
            
            // Check for both grid selections and slider selections
            const selectedOptionsInModal = modal.querySelectorAll('.customizer-option.selected, .customizer-product-option.selected').length;
            const selectedProductsInSlider = window.customizerCollectionModalSlider?.selectedProducts?.size || 0;
            const totalSelections = selectedOptionsInModal + selectedProductsInSlider;
            
            if (collectionCard) {
              if (totalSelections > 0) {
                collectionCard.classList.add("selected");
                this.removeNoProductSelectedClass(stepId);
                // Update button text to "View / Modify"
                this.updateCollectionCardButton(collectionCard, "View / Modify");
              } else {
                collectionCard.classList.remove("selected");
                // Reset button text to original
                this.updateCollectionCardButton(collectionCard, '');
              }
            }

            // Save applied selections for this modal
            this.saveAppliedSelections(modalId);
            
            this.updateCardTitle(modalId);
            this.updateStepSummary(modalId);
            this.updateCardPrice(modalId);
            this.handleModalClose(modalId);
            
            // Clean up initial selections
            this.initialModalSelections.delete(modalId);
            this.initialSliderSelections.delete(modalId);
          }
        }
      }


      // Handle other customizer options (non-collection) and no-product buttons
      else if (target.closest(".customizer-option:not(.customizer-collection-option)") || target.closest(".no-product-button")) {
        const item = target.closest(".customizer-option:not(.customizer-collection-option)") || target.closest(".no-product-button");
        const variantId = item.dataset.variantid;
        const stepId = item.dataset.stepId;
        const optionName = item.dataset.optionName;

        // Handle "no product" options. Clear other selections in the same step
        if (optionName && (optionName.toLowerCase().includes('no ') || optionName.toLowerCase().includes('none'))) {
          // Clear all other selections in this step
          this.clearStepSelections(stepId);
          this.updateAddToCartPrice(true);
          // Unselect all other options in this step visually
          const stepElement = document.querySelector(`.step[data-step-id="${stepId}"]`);
          if (stepElement) {
            stepElement.querySelectorAll('.customizer-option').forEach(option => {
              option.classList.add('no-product-selected');
            });
            stepElement.querySelectorAll('.customizer-option.selected').forEach(option => {
              option.classList.remove('selected');
              
              // Reset collection card titles to their original names when no-product is selected
              if (option.classList.contains('customizer-collection-option')) {
                const optionId = option.dataset.optionid || option.dataset.optionID;
                if (optionId) {
                  const modalId = `collection-modal-${optionId}`;

                  // Remove all products from selected products in the slider for this step
                  if (window.customizerCollectionModalSlider && stepId) {
                    window.customizerCollectionModalSlider.removeAllProductsFromSelected(stepId);
                    this.saveAppliedSelections(modalId);
                  }
                  
                  // Clear selected options in the related modal
                  const modal = document.getElementById(modalId);
                  if (modal) {
                    modal.querySelectorAll('.customizer-option.selected, .customizer-product-option.selected').forEach(modalOption => {
                      modalOption.classList.remove('selected');
                    });

                    // Remove all products from selected products in the slider for this step
                    if (window.customizerCollectionModalSlider && stepId) {
                      window.customizerCollectionModalSlider.removeAllProductsFromSelected(stepId);
                      this.saveAppliedSelections(modalId);
                    }

                    this.updateApplyButton(modalId);
                    this.updateSelectedPrice(modalId);
                  }
                  
                  this.updateCardTitle(modalId);
                  this.updateStepSummary(modalId);
                }
              }
            });
          }
          
          // Select the "no product" option
          this.selected.add(variantId);
          item.classList.add("selected");
          this.updateStepSelection(stepId, variantId, true);
        } else {
          // Option logic
          if (this.selected.has(variantId)) {
            this.selected.delete(variantId);
            item.classList.remove("selected");
            this.updateStepSelection(stepId, variantId, false);
          } else {
            // Check if there's a "no product" option selected in this step and remove it
            const stepElement = document.querySelector(`.step[data-step-id="${stepId}"]`);
            if (stepElement) {
              const noProductOptions = stepElement.querySelectorAll('.customizer-option.selected').forEach(option => {
                const name = option.dataset.optionName;
                if (name && (name.toLowerCase().includes('no ') || name.toLowerCase().includes('none'))) {
                  const noProductVariantId = option.dataset.variantid;
                  this.selected.delete(noProductVariantId);
                  option.classList.remove('selected');
                  this.updateStepSelection(stepId, noProductVariantId, false);
                }
              });
            }
            
            this.selected.add(variantId);
            item.classList.add("selected");
            this.updateStepSelection(stepId, variantId, true);
          }
        }

        // Hide step error when selection is made
        if (stepId) {
          this.hideStepError(stepId);
        }
      }
    });



    // Add all selections to cart (both regular and sticky buttons)
    document.querySelectorAll(".single-add-to-cart-button").forEach(button => {
      button.addEventListener("click", async (e) => {
        e.preventDefault(); // Prevent default form submission for all buttons
        await this.addCustomizerProductsToCart();
      });
    });

    // Update price when quantity changes
    const quantityInput = document.querySelector(".customizer-qty-input");
    if (quantityInput) {
      quantityInput.addEventListener("input", (e) => {
        this.updateAddToCartPrice(true);
      });
      
      // Also listen for quantity button clicks (plus/minus buttons)
      const quantityContainer = quantityInput.closest('.quantity');
      if (quantityContainer) {
        const plusButton = quantityContainer.querySelector('.plus');
        const minusButton = quantityContainer.querySelector('.minus');
        
        if (plusButton) {
          plusButton.addEventListener('click', () => {
            setTimeout(() => {
              this.updateAddToCartPrice(true);
            }, 10);
          });
        }
        
        if (minusButton) {
          minusButton.addEventListener('click', () => {
            setTimeout(() => {
              this.updateAddToCartPrice(true);
            }, 10);
          });
        }
      }
    }
  }

  // Shared function to add customizer products to cart
  async addCustomizerProductsToCart() {
      // Validate all steps have selections before adding to cart
      if (!this.validateAllSteps()) {
        return; // Stop if validation fails - errors are already displayed
      }

      const qty = document.querySelector(".customizer-qty-input")?.value || 1;
      const items = [];
      
      // Add the main product variant to cart
      const mainProductVariantId = document.querySelector("select[name='id']")?.value || 
                                   document.querySelector("input[name='id']")?.value;
      if (mainProductVariantId) {
        items.push({
          id: mainProductVariantId,
          quantity: qty
        });
      }
      
      // Add grid selections
      const gridItems = Array.from(this.selected)
        .filter(id => !id.includes('no-product-')) // Exclude no-product items from cart
        .map(id => ({
          id: id,
          quantity: qty
        }));
      items.push(...gridItems);
      
      // Add slider selections
      if (window.customizerCollectionModalSlider && window.customizerCollectionModalSlider.selectedProducts) {
        window.customizerCollectionModalSlider.selectedProducts.forEach((variant, variantId) => {
          items.push({
            id: variantId,
            quantity: variant.quantity
          });
        });
      }

      console.log('Adding items to cart:', items);

      try {
        // Use the same approach as ProductForm
        const config = {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript'
          }
        };
        
        let formData = new FormData();
        
        // Add sections to form data (same as ProductForm)
        const sectionsToRender = this.getSectionsToRender();
        formData.append('sections', sectionsToRender.map((section) => section.section));
        formData.append('sections_url', window.location.pathname);
        
        // Add items to form data
        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, item.id);
          formData.append(`items[${index}][quantity]`, item.quantity);
        });
        
        config.body = formData;

        const response = await fetch("/cart/add.js", config);
        const parsedState = await response.json();

        if (response.ok && !parsedState.status) {
          // Render the updated sections (same as ProductForm)
          this.renderContents(parsedState);
          
          // Open the cart drawer instead of navigating to the cart page
          if (document.getElementById('Cart-Drawer')) {
            document.body.classList.add('open-cc');
            document.body.classList.add('open-cart');
            document.getElementById('Cart-Drawer').classList.add('active');
            document.getElementById('Cart-Drawer').focus();
            dispatchCustomEvent('cart-drawer:open');
          } else {
            // Fallback to cart page if drawer is not available
            window.location.href = "/cart";
          }
        } else {
          console.error("Error adding items to cart:", parsedState.message || response.statusText);
        }
      } catch (err) {
        console.error("Error adding bundle to cart:", err);
      }
  }

  // Get quantity
  getQuantity() {
    const quantityInput = document.querySelector(".customizer-qty-input");
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

    return quantity;
  }

  // Get base product price from any add to cart button
  getBasePrice(quantity) {
    const addToCartButton = document.querySelector(".single-add-to-cart-button");
  
    if (addToCartButton && addToCartButton.dataset.basePrice) {
      return (parseFloat(addToCartButton.dataset.basePrice) || 0) * quantity;
    }

    return 0;
  }

  // Calculate total price including base product and all customizer selections
  calculateTotalPrice(includeCurrentSelections = false) {
    let totalPrice = 0;
    const quantity = this.getQuantity();
    
    totalPrice += this.getBasePrice(quantity);
    
    // Check all modals to find the currently open one
    const allModals = document.querySelectorAll('[id^="collection-modal-"]');
    let currentModal = null;
    let currentModalId = null;
    
    for (const modal of allModals) {
      const style = window.getComputedStyle(modal);
      if (style.display !== 'none' && style.visibility !== 'hidden') {
        currentModal = modal;
        currentModalId = modal.getAttribute('id');
        break;
      }
    }
    
    // Add prices from all applied selections
    this.appliedSelections.forEach((selections, modalId) => {
      // If we're including current selections and this is the currently open modal,
      // skip the applied selections for this modal to avoid double-counting
      if (includeCurrentSelections && modalId === currentModalId) {
        return;
      }
      
      if (selections.grid) {
        selections.grid.forEach(option => {
          totalPrice += (parseFloat(option.price) || 0) * quantity;
        });
      }
      
      if (selections.slider) {
        selections.slider.forEach(variant => {
          const priceValue = parseFloat(variant.price.replace(/[^0-9.]/g, '')) || 0;
          totalPrice += priceValue * variant.quantity;
        });
      }
    });
    
    // Add current selections if requested (for real-time updates)
    if (includeCurrentSelections) {
      // Add selections from current modal if found
      if (currentModal) {
        console.log('currentModal', currentModalId);
        const selectedOptions = currentModal.querySelectorAll('.customizer-option.selected, .customizer-product-option.selected');
        selectedOptions.forEach(option => {
          let price = parseFloat(option.dataset.price) || 0;
          
          if (price === 0) {
            const priceElement = option.querySelector('.customizer-option__price .money, .price .money');
            if (priceElement) {
              const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
              price = parseFloat(priceText) || 0;
            }
          }
          
          totalPrice += price * quantity;
        });
        
        // Also add slider selections from the current modal if they exist
        if (window.customizerCollectionModalSlider && window.customizerCollectionModalSlider.selectedProducts) {          
          window.customizerCollectionModalSlider.selectedProducts.forEach((variant) => {
            const cardInModal = variant.modalId === currentModalId;
            
            if (cardInModal) {
              const priceValue = parseFloat(variant.price.replace(/[^0-9.]/g, '')) || 0;
              totalPrice += priceValue * variant.quantity;
            }
          });
        }
      }
      
      // Add any direct customizer options that are selected (not in modals)
      // Use this.selected Set instead of DOM query to avoid timing issues
      const selectedVariantIds = Array.from(this.selected);
      
      // Find direct customizer options (not in any modal) by checking each selected variant
      selectedVariantIds.forEach(variantId => {
        const optionElement = document.querySelector(`[data-variantid="${variantId}"]`);
        if (optionElement) {
          const isInModal = optionElement.closest('[id^="collection-modal-"]');
          
          // Only count as direct if it's not in any modal
          if (!isInModal) {
            let price = parseFloat(optionElement.dataset.price) || 0;
            
            if (price === 0) {
              const priceElement = optionElement.querySelector('.customizer-option__price .money, .price .money');
              if (priceElement) {
                const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
                price = parseFloat(priceText) || 0;
              }
            }
            
            totalPrice += price * quantity;
          }
        }
      });
    }
    
    return totalPrice;
  }

  // Update the price display in add to cart buttons
  updateAddToCartPrice(includeCurrentSelections = false) {
    const quantity = this.getQuantity();
    const basePrice = this.getBasePrice(quantity);
    const totalPrice = this.variablePricing ? this.calculateTotalPrice(includeCurrentSelections) : basePrice * quantity;
    const formattedPrice = this.formatCurrency(totalPrice);
    
    // Update all customizer price elements
    const priceElements = document.querySelectorAll('.customizer-price');
    priceElements.forEach(element => {
      element.textContent = formattedPrice;
    });
    
    // Show price separator and price in sticky add to cart
    const stickyButton = document.querySelector('.customizer-sticky-add-to-cart-container .price-separator');
    const stickyPrice = document.querySelector('.customizer-sticky-add-to-cart-container .customizer-price');
    if (stickyButton && stickyPrice) {
      stickyButton.style.display = 'inline';
      stickyPrice.style.display = 'inline';
    }
  }

  // Format price with proper USD currency formatting (commas for thousands)
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}

document.addEventListener("DOMContentLoaded", function() {
  window.customizer = new Customizer();
});